import { graphqlRequest } from "./graphql";

// ── Order Row ──

export interface OrderRow {
  id: string;
  coupon: string;
  pin: string | null;
  created_at: string;
  updated_at: string;
  expiring_at: string | null;
  plan_id: string | null;
  service_id: string | null;
  allocated_at: string | null;
  delivery_status: string;
  amount: number;
  avail_conditions: string | null;
  discounted_amount: number;
  whatsub_service: { service_name: string; image_url: string } | null;
  whatsub_plan: {
    plan_name: string;
    whatsub_service: { service_name: string; image_url: string };
  } | null;
}

export function getOrderServiceName(order: OrderRow): string {
  return (
    order.whatsub_service?.service_name ??
    order.whatsub_plan?.whatsub_service?.service_name ??
    "Unknown"
  );
}

export function getOrderServiceImage(order: OrderRow): string {
  return (
    order.whatsub_service?.image_url ??
    order.whatsub_plan?.whatsub_service?.image_url ??
    ""
  );
}

export function getOrderPlanName(order: OrderRow): string {
  return order.whatsub_plan?.plan_name ?? "—";
}

// ── Filters ──

export interface OrderFilters {
  userId: string;
  statuses?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
}

function buildOrderWhere(filters: OrderFilters): Record<string, unknown> {
  const where: Record<string, unknown> = {
    user_id: { _eq: filters.userId },
  };
  if (filters.statuses && filters.statuses.length > 0) {
    where["delivery_status"] = { _in: filters.statuses };
  }
  const createdAt: Record<string, string> = {};
  if (filters.createdFrom) createdAt["_gte"] = filters.createdFrom;
  if (filters.createdTo) createdAt["_lte"] = filters.createdTo;
  if (Object.keys(createdAt).length > 0) {
    where["allocated_at"] = createdAt;
  }
  if (filters.search) {
    where["_or"] = [
      { whatsub_service: { service_name: { _ilike: `%${filters.search}%` } } },
      { whatsub_plan: { whatsub_service: { service_name: { _ilike: `%${filters.search}%` } } } },
      { coupon: { _ilike: `%${filters.search}%` } },
    ];
  }
  return where;
}

// ── Paginated Orders ──

interface GetOrderHistoryResponse {
  whatsub_coupon_allocation: OrderRow[];
  whatsub_coupon_allocation_aggregate: { aggregate: { count: number } };
}

export async function getOrderHistory(
  filters: OrderFilters,
  limit: number,
  offset: number,
  token: string
) {
  const query = `
    query GetOrderHistory($limit: Int!, $offset: Int!, $where: whatsub_coupon_allocation_bool_exp) {
      whatsub_coupon_allocation(
        order_by: { allocated_at: desc }
        where: $where
        limit: $limit
        offset: $offset
      ) {
        id
        coupon
        pin
        created_at
        updated_at
        expiring_at
        plan_id
        service_id
        allocated_at
        delivery_status
        amount
        avail_conditions
        discounted_amount
        whatsub_service {
          service_name
          image_url
        }
        whatsub_plan {
          plan_name
          whatsub_service {
            service_name
            image_url
          }
        }
      }
      whatsub_coupon_allocation_aggregate(where: $where) {
        aggregate { count }
      }
    }
  `;
  const where = buildOrderWhere(filters);
  const data = await graphqlRequest<GetOrderHistoryResponse>(
    query,
    { limit, offset, where },
    token
  );
  return {
    rows: data.whatsub_coupon_allocation,
    total: data.whatsub_coupon_allocation_aggregate.aggregate.count,
  };
}

// ── All Orders (for export) ──

interface GetAllOrdersResponse {
  whatsub_coupon_allocation: OrderRow[];
}

export async function getAllOrders(filters: OrderFilters, token: string) {
  const query = `
    query GetAllOrders($where: whatsub_coupon_allocation_bool_exp) {
      whatsub_coupon_allocation(
        order_by: { allocated_at: desc }
        where: $where
      ) {
        id
        coupon
        pin
        created_at
        updated_at
        expiring_at
        plan_id
        service_id
        allocated_at
        delivery_status
        amount
        discounted_amount
        whatsub_service {
          service_name
          image_url
        }
        whatsub_plan {
          plan_name
          whatsub_service {
            service_name
            image_url
          }
        }
      }
    }
  `;
  const where = buildOrderWhere(filters);
  const data = await graphqlRequest<GetAllOrdersResponse>(query, { where }, token);
  return data.whatsub_coupon_allocation;
}

// ── Unique Statuses ──

interface GetStatusesResponse {
  whatsub_coupon_allocation: Array<{ delivery_status: string }>;
}

export async function getOrderStatuses(userId: string, token: string) {
  const query = `
    query GetUniqueOrderStatuses($user_id: uuid!) {
      whatsub_coupon_allocation(
        where: { user_id: { _eq: $user_id } }
        distinct_on: delivery_status
      ) {
        delivery_status
      }
    }
  `;
  const data = await graphqlRequest<GetStatusesResponse>(
    query,
    { user_id: userId },
    token
  );
  return data.whatsub_coupon_allocation.map((s) => s.delivery_status);
}
